import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content: "Hi! I'm your OG Solutions assistant. Ask me anything about your audit reports, your subscription, or what to focus on next.",
};

const renderContent = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );

export const CustomerAssistant = () => {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/me/chat/history`, { withCredentials: true })
      .then((r) => {
        if (r.data.messages.length) {
          setMessages([GREETING, ...r.data.messages.map((m) => ({ role: m.role, content: m.content }))]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/me/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta || payload.error) {
            const chunk = payload.delta || payload.error;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Something went wrong — email owengraham5546@gmail.com and Owen will help directly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 overflow-hidden" data-testid="customer-assistant">
      <div className="px-8 pt-8 pb-4">
        <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 flex items-center gap-2">
          <Sparkles className="h-4 w-4" /> Ask About Your Reports
        </p>
      </div>
      <div ref={listRef} className="h-72 overflow-y-auto px-8 pb-4 space-y-3" data-testid="customer-assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === "user"
                ? "bg-white text-[#060D1A] rounded-br-md"
                : "bg-white/[0.07] text-slate-200 rounded-bl-md"
            }`}>
              {m.content ? renderContent(m.content) : (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="border-t border-white/10 p-4 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your latest report…"
          data-testid="customer-assistant-input"
          className="flex-1 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          data-testid="customer-assistant-send"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
          aria-label="Send message"
        >
          {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};
